import React, {useEffect, useState} from 'react';
import {Box, Button, Card, CardContent, Grid, Typography, useToastProvider} from '@deere/fuel-react';
import "../assets/ListItem.scss";
import {useForm} from "react-hook-form";
import {useHistory, useParams} from 'react-router-dom';
import {toCapitalizeSplitStr} from '../shared/utils';
import {useAuthConfig} from "../shared/helper";
import {useOktaAuth} from '@okta/okta-react';
import {Loading} from "../shared/Loading";
import {NoCriteriaInfo} from '../shared/NoCriteriaInfo';
import {FeedbackRespType} from "./FeedbackRespType";
import {FEEDBACK_FORM_STATE, NO_CRITERIA_VIEW_INFO} from "../shared/Constants";
import {FeedbackViewComments} from "./FeedbackViewComments";
import {useUserInfo} from "../shared/UserInfo";


export const FeedbackView = ({name}) => {
    let history = useHistory();
    const {authState} = useOktaAuth();
    let authAxios = useAuthConfig();
    const {enqueueToast} = useToastProvider();
    let {editFeedbackId} = useParams();
    const {control} = useForm();
    const [state, setState] = useState(FEEDBACK_FORM_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const user = useUserInfo();

    const handleBack = () => {
        history.push("/feedback");
    }

    const canReopen = () => {
        const today = new Date();
        const due = new Date(state?.dueDate);
        //Set dueDate time to the end of the day
        due.setHours(24);
        return (due - today >= 0) && (user?.userId === state?.requestedById || user?.isCriteriaAdmin)
    }
    const handleReopen = async () => {
        await authAxios.put(`/feedback/${state.campaignId}/${user.userId}/reopen`)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: res.data,
                    isCloseable: true
                });
                setTimeout(() => {
                    history.push(`/feedback/submit-feedback/${state.campaignId}`);
                }, 1500);
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "Error Reopening Feedback",
                    message:error.response.data,
                    isPersistent: true
                });
            });
    }

    const getCampaignData = async (id) => {
        return await authAxios.get(`feedbacks/${id}`);
    }

    const processData = data => {
        setState({
            ...state,
            title: data.campaignTitle,
            campaignId: data.campaignId,
            supplierName: data.supplierName,
            supplierNumber: data.supplierNumber,
            period: data.period,
            fiscalYear: data.fiscalYear,
            dueDate: data.dueDate,
            requestedByName: data.requestedByName,
            requestedById: data.requestedById,
            responseStatus: data.responseStatus,
            qualityCriteriaData: data.criteria.filter(item => item.category.id === 1),
            deliverCriteriaData: data.criteria.filter(item => item.category.id === 2),
            processAlignmentCriteriaData: data.criteria.filter(item => item.category.id === 3),
            relationshipCriteriaData: data.criteria.filter(item => item.category.id === 4),
            valueCreationCriteriaData: data.criteria.filter(item => item.category.id === 5),
            criteriaData: data.criteria,
            qualityGeneralComments: data.generalComments.filter(item => item.categoryId === "1").length > 0 ? data.generalComments.filter(item => item.categoryId === "1")[0].comment : "",
            deliveryGeneralComments: data.generalComments.filter(item => item.categoryId === "2").length > 0 ? data.generalComments.filter(item => item.categoryId === "2")[0].comment : "",
            processGeneralComments: data.generalComments.filter(item => item.categoryId === "3").length > 0 ? data.generalComments.filter(item => item.categoryId === "3")[0].comment : "",
            relationshipGeneralComments: data.generalComments.filter(item => item.categoryId === "4").length > 0 ? data.generalComments.filter(item => item.categoryId === "4")[0].comment : "",
            valueCreationGeneralComments: data.generalComments.filter(item => item.categoryId === "5").length > 0 ? data.generalComments.filter(item => item.categoryId === "5")[0].comment : "",
        });
    }

    useEffect(() => {
        setIsLoading(true)
        if (editFeedbackId) {
            getCampaignData(editFeedbackId).then(res => {
                const data = res.data;
                processData(data)
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
            })
        }
        return () => {
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <>
            {isLoading && (
                <Loading/>
            )}
            {!isLoading && (
                <Grid container>
                    <Grid item xs={12} md={7} className="sticky-header">
                        <Typography variant="h1" component="h1">
                            {state.title ? state.title : `${toCapitalizeSplitStr(state.supplierName)} - ${parseInt(state.supplierNumber)}`}
                        </Typography>
                        <Typography variant="h4">
                            Requested By {state.requestedByName} - {state.requestedById}, Submitted on
                            by {state.dueDate}
                        </Typography>
                    </Grid>
                    <Grid container item sx={{}} xs={12} md={5} className="sticky-header" justifyContent="flex-end">
                        {canReopen() && <Box justifyContent="flex-end" mr={5}>
                            <Button variant="primary" size="large"
                                    onClick={() => handleReopen()}
                            >Reopen</Button>
                        </Box>}
                        <Box justifyContent="flex-end">
                            <Button variant="primary" size="large"
                                    onClick={() => handleBack()}
                            >Back</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Box m={6}>
                            <Card>
                                <CardContent style={{padding: 0}}>
                                    <Box p={4}>
                                        <Grid container alignItems="center" spacing={4}>
                                            <Grid item xs={12}>
                                                <Typography variant="h2">
                                                    Quality
                                                </Typography>
                                            </Grid>
                                            {state.qualityCriteriaData && state.qualityCriteriaData.length ?
                                                state.qualityCriteriaData && state.qualityCriteriaData.map((item, index) => {
                                                    return (
                                                        <FeedbackRespType
                                                            key={index}
                                                            state={state}
                                                            control={control}
                                                            item={item}
                                                        />
                                                    );
                                                }) :
                                                <NoCriteriaInfo info={NO_CRITERIA_VIEW_INFO}/>
                                            }
                                            <Grid item xs={12}>
                                                <Typography variant="label">
                                                    General Comments
                                                </Typography>
                                                <FeedbackViewComments comments={state.qualityGeneralComments}/>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box m={6}>
                            <Card>
                                <CardContent style={{padding: 0}}>
                                    <Box p={4}>
                                        <Grid container alignItems="center" spacing={4}>
                                            <Grid item xs={12}>
                                                <Typography variant="h2">
                                                    Delivery
                                                </Typography>
                                            </Grid>
                                            {state.deliverCriteriaData && state.deliverCriteriaData.length ?
                                                state.deliverCriteriaData && state.deliverCriteriaData.map((item, index) => {
                                                    return (
                                                        <FeedbackRespType
                                                            key={index}
                                                            state={state}
                                                            control={control}
                                                            item={item}
                                                        />
                                                    );
                                                }) :
                                                <NoCriteriaInfo info={NO_CRITERIA_VIEW_INFO}/>
                                            }
                                            <Grid item xs={12}>
                                                <Typography variant="label">
                                                    General Comments
                                                </Typography>
                                                <FeedbackViewComments comments={state.deliveryGeneralComments}/>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box m={6}>
                            <Card>
                                <CardContent style={{padding: 0}}>
                                    <Box p={4}>
                                        <Grid container alignItems="center" spacing={4}>
                                            <Grid item xs={12}>
                                                <Typography variant="h2">
                                                    Process Alignment
                                                </Typography>
                                            </Grid>
                                            {state.processAlignmentCriteriaData && state.processAlignmentCriteriaData.length ?
                                                state.processAlignmentCriteriaData && state.processAlignmentCriteriaData.map((item, index) => {
                                                    return (
                                                        <FeedbackRespType
                                                            key={index}
                                                            state={state}
                                                            control={control}
                                                            item={item}
                                                        />
                                                    )
                                                }) :
                                                <NoCriteriaInfo info={NO_CRITERIA_VIEW_INFO}/>
                                            }
                                            <Grid item xs={12}>
                                                <Typography variant="label">
                                                    General Comments
                                                </Typography>
                                                <FeedbackViewComments comments={state.processGeneralComments}/>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box m={6}>
                            <Card>
                                <CardContent style={{padding: 0}}>
                                    <Box p={4}>
                                        <Grid container alignItems="center" spacing={4}>
                                            <Grid item xs={12}>
                                                <Typography variant="h2">
                                                    Relationship
                                                </Typography>
                                            </Grid>
                                            {state.relationshipCriteriaData && state.relationshipCriteriaData.length ?
                                                state.relationshipCriteriaData && state.relationshipCriteriaData.map((item, index) => {
                                                    return (
                                                        <FeedbackRespType
                                                            key={index}
                                                            state={state}
                                                            control={control}
                                                            item={item}
                                                        />
                                                    )
                                                }) :
                                                <NoCriteriaInfo info={NO_CRITERIA_VIEW_INFO}/>
                                            }
                                            <Grid item xs={12}>
                                                <Typography variant="label">
                                                    General Comments
                                                </Typography>
                                                <FeedbackViewComments comments={state.relationshipGeneralComments}/>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box m={6}>
                            <Card>
                                <CardContent style={{padding: 0}}>
                                    <Box p={4}>
                                        <Grid container alignItems="center" spacing={4}>
                                            <Grid item xs={12}>
                                                <Typography variant="h2">
                                                    Value Creation
                                                </Typography>
                                            </Grid>
                                            {state.valueCreationCriteriaData && state.valueCreationCriteriaData.length ?
                                                state.valueCreationCriteriaData && state.valueCreationCriteriaData.map((item, index) => {
                                                    return (
                                                        <FeedbackRespType
                                                            key={index}
                                                            state={state}
                                                            control={control}
                                                            item={item}
                                                        />
                                                    )
                                                }) :
                                                <NoCriteriaInfo info={NO_CRITERIA_VIEW_INFO}/>
                                            }
                                            <Grid item xs={12}>
                                                <Typography variant="label">
                                                    General Comments
                                                </Typography>
                                                <FeedbackViewComments comments={state.valueCreationGeneralComments}/>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </>
    );
}
